import { HistoryLevel, IndicatorState } from '@/types';

/**
 * The page's single source of truth for what each state looks like.
 *
 * Every state carries FOUR independent signals: a word, a distinct icon SHAPE,
 * a colour, and (via sorting) a position. Colour alone is never load-bearing —
 * roughly one in twelve men cannot separate the green and amber dots that most
 * status pages lean on, and this page is read under stress.
 *
 * `unknown` is a first-class member rather than a fallback, because the failure
 * this page keeps having is rendering absence of data as good news.
 */
export interface IndicatorTokens {
  /** The word. Always rendered — never an icon on its own. */
  label: string;
  /** Headline sentence for the aggregate verdict. */
  headline: string;
  /** Solid fill for dots and history bars. */
  dot: string;
  /** Text colour for the status word. */
  text: string;
  /** Tinted surface for banners and chips. */
  surface: string;
  /** Border that matches the surface. */
  border: string;
}

export const INDICATORS: Record<IndicatorState, IndicatorTokens> = {
  operational: {
    label: 'Operational',
    headline: 'All systems operational',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    surface: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  degraded: {
    label: 'Degraded',
    headline: 'Some systems degraded',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    surface: 'bg-amber-50',
    border: 'border-amber-300',
  },
  outage: {
    label: 'Outage',
    headline: 'Service outage',
    dot: 'bg-red-500',
    text: 'text-red-700',
    surface: 'bg-red-50',
    border: 'border-red-300',
  },
  unknown: {
    label: 'Unknown',
    headline: 'Status unavailable',
    dot: 'bg-slate-300',
    text: 'text-slate-500',
    surface: 'bg-slate-100',
    border: 'border-slate-300',
  },
};

export function getIndicator(state: IndicatorState): IndicatorTokens {
  return INDICATORS[state];
}

/**
 * Bar colours for the fault-history strip.
 *
 * `quiet` is slate, NOT green, and that is deliberate. The aggregate records
 * failures only — it has no success or traffic signal — so an hour with no
 * entries means "nothing was reported", which is a weaker claim than "it was
 * up". Painting it green would be the same lie this page has already told
 * three times. `uncovered` is fainter still: those hours were discarded by the
 * backend's size cap and were never ours to report on.
 */
export const HISTORY_BAR_COLORS: Record<HistoryLevel, string> = {
  quiet: 'bg-slate-200',
  errors: 'bg-amber-400',
  critical: 'bg-red-500',
  uncovered: 'bg-slate-100',
};

export const HISTORY_BAR_LABELS: Record<HistoryLevel, string> = {
  quiet: 'no errors reported',
  errors: 'errors reported',
  critical: 'critical errors reported',
  uncovered: 'no data retained',
};
